// ─── Mapping pur historique persisté → modèle frontend (story 5.5) ───────────
// + reducer pur d'un événement SSE sur le message assistant courant.

import {
  createLocalId,
  isActionCardPreview,
  isActionNavigateLink,
  isToolResultPayload,
  PENDING_ACTION_TTL_MS,
  type ActionCardData,
  type ActionNavigateLink,
  type ActionOutcomePayload,
  type AgentChatMessage,
  type ChatSSEEvent,
  type PersistedChatMessage,
  type ToolInvocation,
} from './chatTypes'

const INTERRUPTED_TOOL_MESSAGE = 'Exécution interrompue'

function safeParseArgs(raw: unknown): unknown {
  if (typeof raw !== 'string') return raw ?? {}
  try {
    return JSON.parse(raw.length > 0 ? raw : '{}')
  } catch {
    return raw
  }
}

function toolStatusFromResult(result: unknown): Pick<ToolInvocation, 'status' | 'errorMessage'> {
  if (isToolResultPayload(result) && result.success) return { status: 'success' }
  const errorMessage =
    isToolResultPayload(result) && typeof result.error === 'string' ? result.error : undefined
  return { status: 'error', errorMessage }
}

// Carte Tier 2/3 dérivée d'un ToolResult (miroir de `toActionCard` backend).
export function actionCardFromResult(
  result: unknown,
  name: string,
  overrides: Partial<ActionCardData> = {}
): ActionCardData | null {
  if (!isToolResultPayload(result)) return null
  const tier = result.confirmationTier
  if (tier !== 2 && tier !== 3) return null
  return {
    id: createLocalId(),
    tier,
    title: typeof result.confirmationMessage === 'string' ? result.confirmationMessage : '',
    description: typeof result.confirmationDetail === 'string' ? result.confirmationDetail : '',
    name,
    state: 'proposed',
    ...(typeof result.pendingActionId === 'string' ? { actionId: result.pendingActionId } : {}),
    ...(typeof result.confirmationPrimaryLabel === 'string' && result.confirmationPrimaryLabel !== ''
      ? { primaryLabel: result.confirmationPrimaryLabel }
      : {}),
    ...(isActionCardPreview(result.confirmationPreview) ? { preview: result.confirmationPreview } : {}),
    ...overrides,
  }
}

function isOutcome(value: unknown): value is ActionOutcomePayload {
  if (typeof value !== 'object' || value === null) return false
  const state = (value as { state?: unknown }).state
  return state === 'executed' || state === 'failed' || state === 'cancelled'
}

// Story 7.1 (AC 13) — état d'une carte d'historique : issue persistée
// (executed / failed / cancelled) ; sinon `proposed` actionnable si l'action
// est encore dans sa durée de vie ; sinon `expired` (« Non confirmée »).
// Une carte `proposed` peut viser une action que le serveur ne connaît plus
// (redémarrage : actions en mémoire, PO Q1) : le 404 renvoyé au clic est alors
// rendu `expired` par `useActionConfirmation` (jamais `failed`).
function historyCardState(
  result: unknown,
  outcome: unknown,
  createdAtMs: number,
  now: number
): Partial<ActionCardData> {
  if (isOutcome(outcome)) {
    return {
      state: outcome.state,
      readOnly: true,
      ...(outcome.resultText ? { resultText: outcome.resultText } : {}),
      ...(outcome.error ? { errorText: outcome.error } : {}),
      ...(outcome.state === 'executed' && isActionNavigateLink(outcome.navigate)
        ? { navigate: outcome.navigate }
        : {}),
    }
  }
  const actionId = isToolResultPayload(result) ? result.pendingActionId : undefined
  if (typeof actionId === 'string' && now - createdAtMs < PENDING_ACTION_TTL_MS) {
    return { state: 'proposed', readOnly: false, actionId }
  }
  return { state: 'expired', readOnly: true }
}

// Historique `GET /api/chat/:id` → messages frontend. Corrélation exacte
// tool_call ↔ tool_result par `toolCallId`. Cartes Tier 2/3 : état réel
// (Story 7.1, cf. `historyCardState`). Rôles autres que user/assistant ignorés.
export function mapPersistedMessages(
  rows: PersistedChatMessage[],
  now: number = Date.now()
): AgentChatMessage[] {
  const out: AgentChatMessage[] = []
  for (const row of rows) {
    if (row.role === 'user') {
      out.push({
        id: row.id,
        role: 'user',
        content: row.content,
        createdAt: row.createdAt,
        status: 'sent',
      })
      continue
    }
    if (row.role !== 'assistant') continue

    const startedAt = Date.parse(row.createdAt) || 0
    const resultsById = new Map<string, unknown>()
    const outcomesById = new Map<string, unknown>()
    for (const r of Array.isArray(row.toolResults) ? row.toolResults : []) {
      if (r && typeof r.toolCallId === 'string') {
        resultsById.set(r.toolCallId, r.result)
        outcomesById.set(r.toolCallId, r.outcome)
      }
    }

    const toolInvocations: ToolInvocation[] = []
    const actionCards: ActionCardData[] = []
    for (const call of Array.isArray(row.toolCalls) ? row.toolCalls : []) {
      if (!call || typeof call.name !== 'string') continue
      const hasResult = resultsById.has(call.id)
      const result = resultsById.get(call.id)
      toolInvocations.push({
        id: call.id,
        name: call.name,
        args: safeParseArgs(call.arguments),
        ...(hasResult
          ? toolStatusFromResult(result)
          : { status: 'error' as const, errorMessage: INTERRUPTED_TOOL_MESSAGE }),
        result,
        startedAt,
      })
      if (hasResult) {
        const card = actionCardFromResult(result, call.name, {
          id: `${row.id}-${call.id}`,
          toolCallId: call.id,
          ...historyCardState(result, outcomesById.get(call.id), startedAt, now),
        })
        if (card) actionCards.push(card)
      }
    }

    // Ligne assistant vide (ni texte, ni tool, ni carte) : pas de bulle fantôme.
    const hasContent = typeof row.content === 'string' && row.content.trim().length > 0
    if (!hasContent && toolInvocations.length === 0 && actionCards.length === 0) continue

    out.push({
      id: row.id,
      role: 'assistant',
      content: row.content,
      createdAt: row.createdAt,
      status: 'done',
      ...(toolInvocations.length ? { toolInvocations } : {}),
      ...(actionCards.length ? { actionCards } : {}),
    })
  }
  return out
}

// Reducer pur d'un événement SSE sur le message assistant en cours.
// `suggestion_pills` et `error` sont gérés par l'appelant (état hors message).
export function applyChatEvent(
  message: AgentChatMessage,
  event: ChatSSEEvent,
  now: number = Date.now()
): AgentChatMessage {
  switch (event.type) {
    case 'content':
      return { ...message, content: message.content + event.content }
    case 'tool_call':
      return {
        ...message,
        toolInvocations: [
          ...(message.toolInvocations ?? []),
          {
            id: event.toolCallId ?? createLocalId(),
            name: event.name,
            args: event.arguments,
            status: 'running',
            startedAt: now,
          },
        ],
      }
    case 'tool_result': {
      // Corrélation exacte par `toolCallId` (story 6.2) ; repli FIFO sur la plus
      // ancienne invocation `running` du même nom quand l'id est absent ou
      // inconnu (backend antérieur).
      const list = message.toolInvocations ?? []
      let idx = event.toolCallId
        ? list.findIndex((t) => t.status === 'running' && t.id === event.toolCallId)
        : -1
      // Repli FIFO uniquement si AUCUNE invocation ne porte cet id ; si l'id
      // correspond à une invocation déjà terminée, l'événement est ignoré.
      const idKnown = event.toolCallId ? list.some((t) => t.id === event.toolCallId) : false
      if (idx === -1 && idKnown) return message
      if (idx === -1) {
        idx = list.findIndex((t) => t.status === 'running' && t.name === event.name)
      }
      const patch = { ...toolStatusFromResult(event.result), result: event.result }
      if (idx === -1) {
        return {
          ...message,
          toolInvocations: [
            ...list,
            {
              id: event.toolCallId ?? createLocalId(),
              name: event.name,
              args: undefined,
              startedAt: now,
              ...patch,
            },
          ],
        }
      }
      return {
        ...message,
        toolInvocations: list.map((t, i) => (i === idx ? { ...t, ...patch } : t)),
      }
    }
    case 'action_card':
      return {
        ...message,
        actionCards: [
          ...(message.actionCards ?? []),
          {
            id: createLocalId(),
            tier: event.tier,
            title: event.title,
            description: event.description,
            name: event.name,
            state: 'proposed',
            ...(event.actionId ? { actionId: event.actionId } : {}),
            ...(event.toolCallId ? { toolCallId: event.toolCallId } : {}),
            ...(event.primaryLabel ? { primaryLabel: event.primaryLabel } : {}),
            ...(isActionCardPreview(event.preview) ? { preview: event.preview } : {}),
          },
        ],
      }
    case 'error':
      return { ...message, status: 'error', errorMessage: event.error }
    default:
      return message
  }
}

// Termine les invocations restées `running` (flux terminé / coupé / stoppé).
export function settleRunningTools(message: AgentChatMessage): AgentChatMessage {
  if (!message.toolInvocations?.some((t) => t.status === 'running')) return message
  return {
    ...message,
    toolInvocations: message.toolInvocations.map((t) =>
      t.status === 'running' ? { ...t, status: 'error', errorMessage: INTERRUPTED_TOOL_MESSAGE } : t
    ),
  }
}

// ─── Liens de navigation Tier 1 (Story 7.3) ──────────────────────────────────
// `data.navigate = { route, label }` d'un résultat de tool Tier 1 réussi
// (getSupplierStatus, getCampaignStatus, listCampaigns) → bouton sous la réponse.
export function extractNavigateLinks(
  invocations: ToolInvocation[] | undefined,
  max = 2
): ActionNavigateLink[] {
  const links: ActionNavigateLink[] = []
  for (const inv of invocations ?? []) {
    if (links.length >= max) break
    if (inv.status !== 'success') continue
    const result = inv.result
    if (!isToolResultPayload(result) || !result.success) continue
    if (result.confirmationTier === 2 || result.confirmationTier === 3) continue
    const nav = (result.data as { navigate?: unknown } | null | undefined)?.navigate
    if (!isActionNavigateLink(nav)) continue
    if (links.some((l) => l.route === nav.route)) continue
    links.push({ route: nav.route, label: nav.label })
  }
  return links
}
